import { parseISO } from "date-fns";
import React from "react";

import { Chip, Skeleton, Table, Text, formatDateTime } from "@performyard/py-components";
import type { PYColumnDef, SortChangedEvent } from "@performyard/py-components";
import type { WorkflowTodoListItem, WorkflowTodoStatus } from "@performyard/service-contracts";

type SortDirection = "asc" | "desc";

const HEADER_HEIGHT = 44;
const ROW_HEIGHT = 48;
const EMPTY_STATE_HEIGHT = 356;
const SKELETON_ROW_COUNT = 5;

const STATUS_CHIP_CONFIG: Record<WorkflowTodoStatus, { label: string; color: "primary" | "green" | "red" }> = {
	active: { label: "Active", color: "primary" },
	completed: { label: "Completed", color: "green" },
	overdue: { label: "Overdue", color: "red" },
};

type AllTodosTableProps = {
	todos: WorkflowTodoListItem[];
	isLoading: boolean;
	onSortChange: (sort: { name: string; direction: SortDirection }) => void;
	activeSort: string;
	activeSortDirection: SortDirection;
	emptyStateText: string;
};

const isLoadingRow = (data: Partial<WorkflowTodoListItem>): boolean => !data._id;

// Todo due dates are stored at midnight UTC and completion dates were historically
// shown as UTC calendar days, so format the date portion directly instead of
// converting to the viewer's timezone (which can shift the day).
const formatUtcDate = (value: unknown): string => {
	if (!value) {
		return "";
	}
	return formatDateTime(parseISO(String(value).slice(0, 10)), { hasTime: false, dateSize: "medium" }) ?? "";
};

const AllTodosTable = ({
	todos,
	isLoading,
	onSortChange,
	activeSort,
	activeSortDirection,
	emptyStateText,
}: AllTodosTableProps) => {
	const onSortChanged = (event: SortChangedEvent<WorkflowTodoListItem>) => {
		const columnState = event.api.getColumnState();
		const sortedColumn = columnState.find((col) => col.sort);
		if (sortedColumn?.colId) {
			onSortChange({
				name: sortedColumn.colId,
				direction: sortedColumn.sort as SortDirection,
			});
		}
	};

	const columns: PYColumnDef<WorkflowTodoListItem>[] = [
		{
			colId: "action",
			headerName: "To Do",
			flex: 1,
			minWidth: 200,
			sortable: true,
			cellRenderer: (params: { data: WorkflowTodoListItem }) => {
				const row = params.data;
				if (isLoadingRow(row)) return <Skeleton className="h-4 w-3/4" />;
				return (
					<Text size="paragraph-3" weight="regular">
						<span className="py-line-clamp-1">{row.action}</span>
					</Text>
				);
			},
		},
		{
			colId: "subject",
			headerName: "Assignee",
			type: "employee",
			width: 180,
			valueGetter: (params: { data?: WorkflowTodoListItem }) => params.data?.subject ?? null,
		},
		{
			colId: "workflow",
			headerName: "Workflow",
			flex: 1,
			minWidth: 180,
			cellRenderer: (params: { data: WorkflowTodoListItem }) => {
				const row = params.data;
				if (isLoadingRow(row)) return <Skeleton className="h-4 w-3/4" />;
				if (!row.workflow) {
					return (
						<Text size="paragraph-3" weight="regular">
							—
						</Text>
					);
				}
				return (
					<a
						href={`/view-workflow/${row.workflow._id}?source=company-workflows`}
						className="text-primary-5 no-underline hover:text-primary-7 hover:underline"
					>
						<Text size="paragraph-3" weight="regular">
							<span className="py-line-clamp-1">{row.workflow.name}</span>
						</Text>
					</a>
				);
			},
		},
		{
			colId: "actor",
			headerName: "Actor",
			type: "employee",
			width: 180,
			valueGetter: (params: { data?: WorkflowTodoListItem }) => params.data?.actor ?? null,
		},
		{
			colId: "due_date",
			headerName: "Due Date",
			type: "date",
			width: 140,
			sortable: true,
			valueGetter: (params: { data?: WorkflowTodoListItem }) => params.data?.dueDate ?? null,
			valueFormatter: (params: { value: unknown }) => formatUtcDate(params.value),
		},
		{
			colId: "date_done",
			headerName: "Completed Date",
			type: "date",
			width: 160,
			sortable: true,
			valueGetter: (params: { data?: WorkflowTodoListItem }) => params.data?.completedDate ?? null,
			valueFormatter: (params: { value: unknown }) => formatUtcDate(params.value),
		},
		{
			colId: "status",
			headerName: "Status",
			width: 130,
			cellRenderer: (params: { data: WorkflowTodoListItem }) => {
				const row = params.data;
				if (isLoadingRow(row)) return <Skeleton className="h-4 w-16" />;
				const { label, color } = STATUS_CHIP_CONFIG[row.status];
				return <Chip label={label} color={color} size={24} />;
			},
		},
	];

	const rowsLength = isLoading ? SKELETON_ROW_COUNT : todos.length;
	const tableWrapperHeight =
		rowsLength === 0 ? `${EMPTY_STATE_HEIGHT}px` : `${HEADER_HEIGHT + rowsLength * ROW_HEIGHT}px`;

	return (
		<div
			data-testid="todos-table"
			style={{
				height: tableWrapperHeight,
				transition: "height 150ms ease-out",
			}}
		>
			<Table<WorkflowTodoListItem>
				columnDefs={columns}
				rowData={todos}
				loading={isLoading}
				getRowId={(params) => params.data._id}
				rowHeight={ROW_HEIGHT}
				defaultColDef={{
					cellStyle: { display: "flex", alignItems: "center" },
					resizable: false,
					sortable: false,
					suppressMovable: true,
					sortingOrder: ["asc", "desc"],
				}}
				emptyStateText={emptyStateText}
				onSortChanged={onSortChanged}
				initialState={{
					sort: {
						sortModel: activeSort ? [{ colId: activeSort, sort: activeSortDirection }] : [],
					},
				}}
			/>
		</div>
	);
};

export default AllTodosTable;
